import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Container, Paper } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WizardProgressBar from '../../Components/WizardProgressBar';
import StepManufacturer from './StepManufacturer';
import StepDevice from './StepDevice';
import StepRepairs from './StepRepairs';
import StepDelivery from './StepDelivery';
import { getData } from '../../Services/getData';
import { postData } from '../../Services/postData';
import {
  UrlEnum,
  findCategoryBySlug,
  findDeviceBySlug,
  getCategorySlug,
  getDeviceSlug,
} from '../../Utils/Utils';
import { Vocabulary } from '../../Utils/Vocabulary';
import { Category, ClientInfo, Device, DeviceRepair, RepairOrderPayload, ShippingInfo } from '../../Utils/types';

interface WizardHistoryState {
  step?: number;
  selectedRepairs?: DeviceRepair[];
  existingRepairDetails?: string;
  otherProblemDescription?: string;
}

export default function Wizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { categorySlug, deviceSlug, deviceId } = useParams<{
    categorySlug?: string;
    deviceSlug?: string;
    deviceId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const preselectRepairId = searchParams.get('repairId') || undefined;
  const [categories, setCategories] = useState<Category[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      getData(UrlEnum.publicCategories),
      getData(UrlEnum.publicDevices),
    ]).then(([categoriesRes, devicesRes]) => {
      if (categoriesRes?.data) setCategories(categoriesRes.data);
      if (devicesRes?.data) setDevices(devicesRes.data);
      setCatalogLoaded(true);
    });
  }, []);

  // The brand and model are resolved from the URL (via slugs) rather than
  // component/history state, so links to a specific brand or model are
  // real, shareable URLs — e.g. for the presentation website to link
  // straight into a brand or a specific device's repair flow.
  //
  // `/device/:deviceId` is a second, id-based way to land on "device already
  // chosen" — used by external deep links (e.g. the repairs catalog
  // shortcode) that know a device's id but not its slug.
  const deviceById = deviceId ? devices.find((d) => d._id === deviceId) || null : null;
  const selectedCategory = categorySlug
    ? findCategoryBySlug(categories, categorySlug)
    : deviceById
    ? categories.find((c) => c._id === deviceById.category) || null
    : null;
  const selectedDevice = deviceById
    ? deviceById
    : selectedCategory && deviceSlug
    ? findDeviceBySlug(devices, selectedCategory._id, deviceSlug)
    : null;

  // A stale/renamed/mistyped brand, model, or device id in the URL falls
  // back to the category list instead of showing a blank step.
  useEffect(() => {
    if (!catalogLoaded) return;
    if (deviceId && !deviceById) {
      toast.error(Vocabulary.modelNotFound);
      navigate('/', { replace: true });
      return;
    }
    if (categorySlug && !selectedCategory) {
      toast.error(Vocabulary.brandNotFound);
      navigate('/', { replace: true });
      return;
    }
    if (deviceSlug && selectedCategory && !selectedDevice) {
      toast.error(Vocabulary.modelNotFound);
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogLoaded, categorySlug, deviceSlug, deviceId, selectedCategory, selectedDevice, deviceById]);

  // Everything past "which device" (selected repairs, delivery step) isn't
  // meant to be link-shareable, so it still lives in history state, same
  // as before — just layered on top of the URL-resolved brand/model/device.
  // `preselectRepairId` is the one exception: an optional, additive seed for
  // StepRepairs' initial selection (not a replacement for history state —
  // once the user continues past this step, selection still flows through
  // history state exactly as before).
  const historyState = (location.state as WizardHistoryState | null) || {};
  const step = deviceId ? historyState.step ?? 2 : !categorySlug ? 0 : !deviceSlug ? 1 : historyState.step ?? 2;
  const selectedRepairs = historyState.selectedRepairs || [];
  const existingRepairDetails = historyState.existingRepairDetails || '';
  const otherProblemDescription = historyState.otherProblemDescription || '';

  function handleSelectCategory(category: Category) {
    navigate(`/${getCategorySlug(category)}`);
  }

  function handleSelectDevice(device: Device) {
    if (!selectedCategory) return;
    const devicesInCategory = devices.filter((d) => d.category === selectedCategory._id);
    navigate(`/${getCategorySlug(selectedCategory)}/${getDeviceSlug(device, devicesInCategory)}`);
  }

  function handleRepairsContinue(data: {
    selectedRepairs: DeviceRepair[];
    existingRepairDetails: string;
    otherProblemDescription: string;
  }) {
    navigate(location.pathname, {
      state: {
        step: 3,
        selectedRepairs: data.selectedRepairs,
        existingRepairDetails: data.existingRepairDetails,
        otherProblemDescription: data.otherProblemDescription,
      },
    });
  }

  async function handleSubmit(data: { clientInfo: ClientInfo; shipping: ShippingInfo }) {
    if (!selectedDevice) return;
    setLoading(true);
    const payload: RepairOrderPayload = {
      clientInfo: data.clientInfo,
      shipping: data.shipping,
      repair: {
        device: {
          id: selectedDevice._id,
          name: selectedDevice.deviceName,
          manufacturer: selectedDevice.manufacturer,
        },
        selectedRepairs,
        existingRepairDetails,
        otherProblemDescription,
      },
    };
    const res = await postData(UrlEnum.publicRepairOrders, payload);
    setLoading(false);
    if (res?.data?.orderNumber || res?.data?._id) {
      navigate('/success', { state: { orderNumber: res.data.orderNumber } });
    } else {
      toast.error(Vocabulary.error);
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 1, px:1 }}>
      <ToastContainer position="top-right" autoClose={4000} />
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <WizardProgressBar activeStep={step} />
        <Box p={4}>
          {step === 0 && (
            <StepManufacturer categories={categories} onSelect={handleSelectCategory} />
          )}
          {step === 1 && selectedCategory && (
            <StepDevice
              devices={devices}
              category={selectedCategory}
              onSelect={handleSelectDevice}
              onBack={() => navigate('/')}
            />
          )}
          {step === 2 && selectedDevice && (
            <StepRepairs
              device={selectedDevice}
              onContinue={handleRepairsContinue}
              onBack={() => navigate(categorySlug ? `/${categorySlug}` : '/')}
              preselectRepairId={preselectRepairId}
            />
          )}
          {step === 3 && (
            <StepDelivery
              onSubmit={handleSubmit}
              onBack={() => navigate(-1)}
              loading={loading}
            />
          )}
        </Box>
      </Paper>
    </Container>
  );
}
